import { Loader2 } from 'lucide-react'
import { Button } from '../../shadcn/button'
import { DialogFooter } from '../../shadcn/dialog'
import { useRef, useEffect } from 'react'
import { useDialogStore } from './dialogStore'

type Variant = 'primary' | 'danger' | 'neutral'

type Props = {
  cancelLabel?: string | React.ReactNode
  confirmLabel?: string | React.ReactNode
  variant?: Variant
  onCancel?: React.MouseEventHandler<HTMLButtonElement>
  onConfirm?: React.MouseEventHandler<HTMLButtonElement>
  showCancel?: boolean
  disabled?: boolean
  loading?: boolean
  content?: React.ReactNode
}

const variantMap = {
  primary: 'default',
  danger: 'destructive',
  neutral: 'secondary',
} as const

export const Footer = ({
  cancelLabel = 'Cancel',
  onCancel,
  onConfirm,
  confirmLabel = 'Confirm',
  variant = 'primary',
  showCancel,
  disabled,
  loading,
  content,
}: Props) => {
  const primaryButtonRef = useRef<HTMLButtonElement>(null)
  const isOpen = useDialogStore((state) => state.show)

  useEffect(() => {
    if (!isOpen || !onConfirm) return

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey && !e.ctrlKey && !e.metaKey && !disabled && !loading) {
        if (primaryButtonRef.current) {
          e.preventDefault()
          primaryButtonRef.current.click()
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, onConfirm, disabled, loading])

  return (
    <DialogFooter className="mt-4 flex gap-2">
      {content}
      {showCancel && !loading && (
        <Button onClick={onCancel} variant={'secondary'} size={'sm'}>
          {cancelLabel}
        </Button>
      )}
      <Button
        onClick={onConfirm}
        ref={primaryButtonRef}
        disabled={disabled || loading}
        variant={variantMap[variant]}
        size={'sm'}
      >
        <span>{confirmLabel}</span>
        {loading && <Loader2 className="h-4 w-4 animate-spin" />}
      </Button>
    </DialogFooter>
  )
}
